// src/firebase.js
import { initializeApp } from 'firebase/app';
import { getAnalytics } from "firebase/analytics";
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// Fail loudly at startup if any required Firebase config env var is missing.
// Without this guard, a missed VITE_ rename would silently pass undefined to
// Firebase, and the app would load fine until a Firebase call fails on a
// specific code path -- the hardest class of bug to detect.
const requiredEnvVars = {
  VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
  VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
};
const missing = Object.entries(requiredEnvVars)
  .filter(([, v]) => !v)
  .map(([k]) => k);
if (missing.length > 0 && process.env.NODE_ENV !== 'test') {
  throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
}

console.log('All env variables:', {
    VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
    NODE_ENV: process.env.NODE_ENV
  });

console.log('Current Project ID:', import.meta.env.VITE_FIREBASE_PROJECT_ID);
console.log('API Key length:', import.meta.env.VITE_FIREBASE_API_KEY?.length || 0);
// Best-practice for OAuth redirect flows (Firebase Auth):
// - Modern browsers increasingly block cross-site storage/cookies.
// - Redirect sign-in relies on Firebase's helper pages under `authDomain` (the `/__/auth/*` endpoints).
// - If your app is served from `*.web.app` (or a custom domain) but `authDomain` is `*.firebaseapp.com`,
//   the flow becomes cross-site and `getRedirectResult()` can silently return null.
// - Therefore, when running on a hosted `*.web.app` origin, we prefer to set `authDomain` to the same
//   origin domain so the helper pages are same-site.
//
// This mirrors Firebase's documented best practice: configure `authDomain` to match the domain that
// serves your app. (You must also ensure that `https://<authDomain>/__/auth/handler` is registered as
// a redirect URI in your OAuth provider.)
const configuredAuthDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
const runtimeHost =
  typeof window !== 'undefined' && window?.location?.hostname ? window.location.hostname : '';
const shouldUseRuntimeHostAsAuthDomain =
  Boolean(runtimeHost) &&
  runtimeHost.endsWith('.web.app') &&
  // Only override when the configured authDomain points at firebaseapp.com (cross-site from web.app).
  Boolean(configuredAuthDomain && configuredAuthDomain.endsWith('.firebaseapp.com'));
const effectiveAuthDomain = shouldUseRuntimeHostAsAuthDomain ? runtimeHost : configuredAuthDomain;

console.log('Auth Domain (configured):', configuredAuthDomain);
console.log('Auth Domain (effective):', effectiveAuthDomain);
console.log('Tenant ID:', import.meta.env.VITE_MS_TENANT_ID);
console.log('Microsoft SSO Test Enable:', import.meta.env.VITE_MS_TEST_ENABLE);
console.log('Microsoft SSO Test Tenant:', import.meta.env.VITE_MS_TEST_TENANT);

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: effectiveAuthDomain,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Log full config shape (without sensitive values)
console.log('Firebase config keys:', Object.keys(firebaseConfig));
console.log('Any undefined config values?', Object.values(firebaseConfig).some(val => val === undefined));

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
// Ensure a consistent persistence model across ALL sign-in methods (email/password + OAuth redirect).
// Without this, redirect-based OAuth flows can behave inconsistently across browsers and dev setups.
setPersistence(auth, browserLocalPersistence).catch((e) => {
  console.warn('Failed to set auth persistence to browserLocalPersistence:', e?.code, e?.message);
});

// App Check, deliberately optional. The site key is registered per Firebase
// project in the console, so environments without one (local dev, preview
// builds, any project where the key has not been created yet) must keep working
// exactly as before: no key means no initialisation and no error. Where a key is
// present, every callable and Firestore request carries an App Check token, and
// the analytics ingest records whether it arrived (envelope
// app_check_verified) without rejecting anything. Nothing here is enforced
// server-side yet - see analytics/SETUP_RUNBOOK.md for the sequence to hard
// enforcement.
//
// Skipped under Jest as well: firebase.js is imported transitively by many
// component tests, and reCAPTCHA has no place in jsdom.
const appCheckSiteKey = import.meta.env.VITE_FIREBASE_APP_CHECK_SITE_KEY;
if (appCheckSiteKey && process.env.NODE_ENV !== 'test') {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(appCheckSiteKey),
      // Keeps a valid token in place for long sittings (a referral form can be
      // open for an hour); without it the token expires mid-session and later
      // requests arrive unattested.
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    // A bad or revoked site key must degrade to unattested requests, never to a
    // blank app: App Check is a data-quality measure here, not a gate.
    console.warn('App Check initialisation skipped:', e?.code, e?.message);
  }
}

export const db = getFirestore(app);
export const analytics = getAnalytics(app);
export const functions = getFunctions(app, 'europe-west2');
export const storage = getStorage(app);
